import { useCallback, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Icon from '../components/Icons';
import { useAuth } from '../context/AuthContext';
import './ErrorPage.css';

const ERROR_CONFIG = {
  404: {
    code: '404',
    icon: 'help',
    tone: 'neutral',
    title: 'Page Not Found',
    description: "The page you are looking for doesn't exist or may have been moved.",
    primary: { label: 'Go to Dashboard', action: 'dashboard', icon: 'home' },
    secondary: { label: 'Go Back', action: 'back', icon: 'chevronLeft' },
  },
  401: {
    code: '401',
    icon: 'lock',
    tone: 'warning',
    title: 'Session Expired',
    description: 'Your session has expired. Please sign in again to continue.',
    primary: { label: 'Login Again', action: 'login', icon: 'logout' },
    redirectNote: 'You will be redirected to the login page shortly.',
  },
  403: {
    code: '403',
    icon: 'shield',
    tone: 'danger',
    title: 'Access Denied',
    description: 'You do not have permission to access this page.',
    primary: { label: 'Go to Dashboard', action: 'dashboard', icon: 'home' },
  },
  500: {
    code: '500',
    icon: 'alert',
    tone: 'danger',
    title: 'Something Went Wrong',
    description: 'An unexpected error occurred while processing your request.',
    primary: { label: 'Retry', action: 'retry', icon: 'refresh' },
    secondary: { label: 'Go to Dashboard', action: 'dashboard', icon: 'home' },
  },
  503: {
    code: '503',
    icon: 'settings',
    tone: 'warning',
    title: "We'll Be Back Soon",
    description: 'The system is currently under maintenance. Please try again later.',
    primary: { label: 'Refresh', action: 'refresh', icon: 'refresh' },
  },
  network: {
    code: null,
    icon: 'alert',
    tone: 'info',
    title: 'Connection Problem',
    description: 'Unable to connect to the server. Please check your internet connection and try again.',
    primary: { label: 'Retry', action: 'retry', icon: 'refresh' },
  },
};

function getConfig(type) {
  return ERROR_CONFIG[type] || ERROR_CONFIG[404];
}

export default function ErrorPage({ type = '404', onRetry, embedded = false }) {
  const navigate = useNavigate();
  const { isSuperAdmin, logout } = useAuth();
  const config = getConfig(type);
  const dashboardPath = isSuperAdmin ? '/home' : '/profile';

  const handleAction = useCallback((action) => {
    if (action === 'login') {
      logout();
      navigate('/login', { replace: true });
      return;
    }

    if (action === 'dashboard') {
      navigate(dashboardPath, { replace: true });
      return;
    }

    if (action === 'back') {
      if (window.history.length > 1) {
        navigate(-1);
      } else {
        navigate(dashboardPath, { replace: true });
      }
      return;
    }

    if (action === 'retry') {
      if (onRetry) {
        onRetry();
      }
      navigate(dashboardPath, { replace: true });
      return;
    }

    if (action === 'refresh') {
      window.location.reload();
    }
  }, [dashboardPath, logout, navigate, onRetry]);

  useEffect(() => {
    if (type !== '401') return undefined;
    const timer = window.setTimeout(() => handleAction('login'), 7000);
    return () => window.clearTimeout(timer);
  }, [handleAction, type]);

  return (
    <section className={`error-page-shell error-page-shell-${config.tone} ${embedded ? 'error-page-shell-embedded' : ''}`.trim()} role="alert">
      <section className="error-page-card" aria-labelledby="error-page-title">
        <div className="error-page-brand">TULSIAN GROUP</div>

        <div className="error-page-visual" aria-hidden="true">
          <div className="error-page-icon">
            <Icon name={config.icon} size={42} strokeWidth={1.7} />
          </div>
          {config.code && <span className="error-page-code">{config.code}</span>}
        </div>

        <div className="error-page-copy">
          <h1 id="error-page-title">{config.title}</h1>
          <p>{config.description}</p>
          {config.redirectNote && (
            <p className="error-page-redirect-note">{config.redirectNote}</p>
          )}
        </div>

        <div className="error-page-actions">
          <button
            type="button"
            className="btn btn-primary btn-lg"
            onClick={() => handleAction(config.primary.action)}
          >
            <Icon name={config.primary.icon} size={18} />
            {config.primary.label}
          </button>
          {config.secondary && (
            <button
              type="button"
              className="btn btn-secondary btn-lg"
              onClick={() => handleAction(config.secondary.action)}
            >
              <Icon name={config.secondary.icon} size={18} />
              {config.secondary.label}
            </button>
          )}
        </div>
      </section>
    </section>
  );
}
